import { useState } from 'react'
import { Link } from 'react-router-dom'
import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { AccountStatement, PostableAccount, ReportBasis } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import {
  Empty,
  ErrorBanner,
  Loading,
  Money,
  Panel,
  Tag,
  formatDate,
  money,
  useDateRange,
} from '../../components/ui'

/** The general ledger for one account, with a running balance in its own natural direction. */
export function AccountStatementPage() {
  const { can } = useAuth()
  const { from, to, setFrom, setTo } = useDateRange()
  const [accountId, setAccountId] = useState('')
  const [basis, setBasis] = useState<ReportBasis>('Accrual')

  const accounts = useQuery<PostableAccount[]>('/accounts/postable')

  const statement = useQuery<AccountStatement>(
    accountId ? `/reports/account-statement/${accountId}${query({ from, to, basis })}` : null,
    [accountId, from, to, basis],
  )

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field" style={{ minWidth: 320 }}>
          <label>Account</label>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} style={{ minWidth: 320 }}>
            <option value="">Choose an account…</option>
            {accounts.data?.map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} · {account.nameEn}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="field">
          <label>Basis</label>
          <select value={basis} onChange={(e) => setBasis(e.target.value as ReportBasis)}>
            <option value="Accrual">Accrual</option>
            <option value="Cash">Cash</option>
          </select>
        </div>

        <div className="spacer" />

        {can(P.reports.export) && accountId && (
          <button
            onClick={() =>
              void download(
                `/reports/account-statement/${accountId}/excel${query({ from, to, basis })}`,
                'account-statement.xlsx',
              )
            }
          >
            Export to Excel
          </button>
        )}
      </div>

      <ErrorBanner error={statement.error} />
      {statement.loading && <Loading />}

      {!accountId && <Empty>Choose an account to see its movements.</Empty>}

      {statement.data && (
        <Panel
          title={
            <h2>
              <span className="code">{statement.data.code}</span> {statement.data.nameEn}
              <span className="muted" style={{ fontWeight: 400 }}>
                {' '}
                · opening {money(statement.data.openingBalance)}
              </span>
            </h2>
          }
          flush
        >
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Date</th>
                  <th style={{ width: 130 }}>Entry</th>
                  <th>Description</th>
                  <th style={{ width: 140 }}>Party</th>
                  <th style={{ width: 90 }}>Basis</th>
                  <th style={{ width: 120 }} className="num">
                    Debit
                  </th>
                  <th style={{ width: 120 }} className="num">
                    Credit
                  </th>
                  <th style={{ width: 130 }} className="num">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {statement.data.rows.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <Empty>No movements in this period.</Empty>
                    </td>
                  </tr>
                )}

                {statement.data.rows.map((row, index) => (
                  <tr key={`${row.journalEntryId}-${index}`}>
                    <td>{formatDate(row.date)}</td>
                    <td className="code">
                      {/* The drill-through the statement exists for: a figure on a report, back to
                          the entry that made it, without reading the number off and searching. */}
                      <Link to={`/journal/${row.journalEntryId}`}>{row.entryNo ?? 'draft'}</Link>
                    </td>
                    <td>
                      {row.description}
                      {row.memo && <span className="muted"> · {row.memo}</span>}
                    </td>
                    <td className="soft">{row.partyName}</td>
                    <td>
                      {row.basis !== 'Both' && <Tag kind="warn">{row.basis}</Tag>}
                    </td>
                    <td className="num">
                      <Money value={row.debit} blankZero />
                    </td>
                    <td className="num">
                      <Money value={row.credit} blankZero />
                    </td>
                    <td className="num strong">
                      <Money value={row.runningBalance} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>Totals</td>
                  <td className="num">
                    <Money value={statement.data.totalDebit} />
                  </td>
                  <td className="num">
                    <Money value={statement.data.totalCredit} />
                  </td>
                  <td className="num">
                    <Money value={statement.data.closingBalance} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Panel>
      )}
    </>
  )
}
